/**
 * NotesListPage Component
 *
 * Modern list view for notes with search, filters, and pagination.
 * Follows the PatientListPage design pattern.
 * Wraps NotesListContent with the standard layout.
 */

import React from 'react';
import { EntitlementUsage } from '@features/entitlements';
import { Alert, AlertTitle, Box, Button } from '@mui/material';

import { useNotesList } from '../hooks/useNotesList';

import { NotesListContent, type NotesListContentProps } from './NotesListContent';

export type NotesListPageProps = NotesListContentProps;

export const NotesListPage: React.FC<NotesListPageProps> = props => {
  const { error } = useNotesList({
    groupId: props.groupId,
    enableDateFilter: props.enableDateFilter,
  });

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading notes</AlertTitle>
          Something went wrong while loading your notes. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1000,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <EntitlementUsage capabilityName="note_creation" source="notes_list" />
      <div>
        <NotesListContent {...props} />
      </div>
    </Box>
  );
};
